import { parseRecordedTimestamp } from "./dateTime.js";

export const LIVE_END_GRACE_MS = 2 * 60 * 1000;
export const LIVE_RECENT_SLICE_MS = 3 * 60 * 1000;

export function historyRowTime(row) {
    const parsed = parseRecordedTimestamp(row?.timestamp);
    return parsed ? parsed.getTime() : null;
}

export function chartNumericValue(value) {
    if (typeof value === "boolean") {
        return value ? 1 : 0;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    return null;
}

export function chartTimePoint(row) {
    const x = historyRowTime(row);
    const y = chartNumericValue(row?.value);
    if (x == null || y == null) {
        return null;
    }
    return { x, y };
}

export function chronologicalSamples(rows) {
    return [...(Array.isArray(rows) ? rows : [])].sort((left, right) => {
        const leftTime = historyRowTime(left);
        const rightTime = historyRowTime(right);
        if (leftTime == null && rightTime == null) {
            return Number(left?.id || 0) - Number(right?.id || 0);
        }
        if (leftTime == null) {
            return 1;
        }
        if (rightTime == null) {
            return -1;
        }
        if (leftTime !== rightTime) {
            return leftTime - rightTime;
        }
        return Number(left?.id || 0) - Number(right?.id || 0);
    });
}

export function liveQueryBounds(nowMs, windowMinutes, { full = true, lastSuccessMs = null } = {}) {
    const minutes = Number(windowMinutes);
    const span = (Number.isFinite(minutes) && minutes > 0 ? minutes : 60) * 60 * 1000;
    const windowStartMs = nowMs - span;
    const windowEndMs = nowMs + LIVE_END_GRACE_MS;
    const gapMs = lastSuccessMs == null ? Number.POSITIVE_INFINITY : nowMs - lastSuccessMs;
    const useFull = Boolean(full) || gapMs > LIVE_RECENT_SLICE_MS;
    return {
        mode: useFull ? "full" : "recent",
        queryStartMs: useFull ? windowStartMs : Math.max(windowStartMs, nowMs - LIVE_RECENT_SLICE_MS),
        queryEndMs: windowEndMs,
        windowStartMs,
        windowEndMs,
    };
}

function hasHistoryId(row) {
    return row?.id != null && row.id !== "";
}

function inWindow(row, windowStartMs, windowEndMs) {
    const time = historyRowTime(row);
    return time != null && time >= windowStartMs && time <= windowEndMs;
}

export function reconcileLiveHistory(existing, incoming, windowStartMs, windowEndMs) {
    const byId = new Map();
    (Array.isArray(existing) ? existing : []).forEach((row) => {
        if (hasHistoryId(row)) {
            byId.set(row.id, row);
        }
    });
    (Array.isArray(incoming) ? incoming : []).forEach((row) => {
        if (!hasHistoryId(row)) {
            return;
        }
        const previous = byId.get(row.id);
        byId.set(row.id, previous ? { ...previous, ...row } : row);
    });
    return chronologicalSamples(
        [...byId.values()].filter((row) => inWindow(row, windowStartMs, windowEndMs))
    );
}

export function reconcileStatusEvents(existing, incoming, windowStartMs, windowEndMs) {
    const byId = new Map();
    (Array.isArray(existing) ? existing : []).forEach((event) => {
        if (hasHistoryId(event)) {
            byId.set(event.id, event);
        }
    });
    (Array.isArray(incoming) ? incoming : []).forEach((event) => {
        if (!hasHistoryId(event)) {
            return;
        }
        const previous = byId.get(event.id);
        byId.set(event.id, previous ? { ...previous, ...event } : event);
    });
    return chronologicalSamples(
        [...byId.values()].filter((event) => inWindow(event, windowStartMs, windowEndMs))
    );
}

function lowerBoundTime(rows, targetMs) {
    let low = 0;
    let high = rows.length;
    while (low < high) {
        const middle = Math.floor((low + high) / 2);
        const time = historyRowTime(rows[middle]);
        if (time != null && time < targetMs) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }
    return low;
}

function insertionIndex(rows, row) {
    const rowTime = historyRowTime(row);
    let low = 0;
    let high = rows.length;
    while (low < high) {
        const middle = Math.floor((low + high) / 2);
        const middleTime = historyRowTime(rows[middle]);
        const before = middleTime < rowTime
            || (middleTime === rowTime && Number(rows[middle]?.id || 0) < Number(row?.id || 0));
        if (before) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }
    return low;
}

export function reconcileIncrementalHistory(existing, incoming, windowStartMs, windowEndMs, previousCursor = 0) {
    const current = Array.isArray(existing) ? existing : [];
    const startIndex = lowerBoundTime(current, windowStartMs);
    const next = startIndex > 0 ? current.slice(startIndex) : [...current];
    const updates = chronologicalSamples(Array.isArray(incoming) ? incoming : []);

    updates.forEach((row) => {
        if (!hasHistoryId(row)) {
            return;
        }
        if (Number(row.id) <= Number(previousCursor || 0)) {
            for (let index = next.length - 1; index >= 0; index -= 1) {
                if (next[index]?.id === row.id) {
                    next.splice(index, 1);
                    break;
                }
            }
        }
        if (!inWindow(row, windowStartMs, windowEndMs)) {
            return;
        }
        const index = insertionIndex(next, row);
        next.splice(index, 0, row);
    });
    return next;
}

export function remapLiveSelection(current, nextRows) {
    if (!current) {
        return null;
    }
    const rows = Array.isArray(nextRows) ? nextRows : [];
    if (current.recordId != null) {
        const found = rows.findIndex((row) => row && row.id === current.recordId);
        return found >= 0
            ? { seriesId: current.seriesId, recordId: current.recordId, index: found }
            : null;
    }
    if (current.timestamp == null) {
        return null;
    }
    const found = rows.findIndex((row) => row && row.timestamp === current.timestamp);
    if (found < 0) {
        return null;
    }
    return { seriesId: current.seriesId, recordId: rows[found].id ?? null, index: found };
}

export function liveTimeExtent(rows) {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const time = historyRowTime(row);
        if (time == null) {
            return;
        }
        if (time < min) {
            min = time;
        }
        if (time > max) {
            max = time;
        }
    });
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return null;
    }
    const span = Math.max(max - min, 1000);
    const pad = Math.max(Math.round(span * 0.04), 1000);
    return { min: min - pad, max: max + pad, latest: max };
}

function liveEdgeTolerance(extent) {
    const span = Math.max(extent.max - extent.min, 1);
    return Math.max(span * 0.04, 1000);
}

export function rangeCoversLiveExtent(min, max, extent) {
    if (!extent || !Number.isFinite(min) || !Number.isFinite(max)) {
        return false;
    }
    const fullSpan = Math.max(extent.max - extent.min, 1);
    const tolerance = liveEdgeTolerance(extent);
    return max - min >= fullSpan * 0.98
        && min <= extent.min + tolerance
        && max >= extent.latest - tolerance;
}

export function liveZoomSource(source, min, max, extent) {
    if (source !== "user") {
        return source || "api";
    }
    return rangeCoversLiveExtent(min, max, extent) ? "api" : "user";
}

export function rememberLiveEdge(previousLatest, scaleMax, extent) {
    if (!extent) {
        return Number.isFinite(previousLatest) ? previousLatest : null;
    }
    const tolerance = liveEdgeTolerance(extent);
    if (!Number.isFinite(scaleMax) || scaleMax >= extent.latest - tolerance) {
        return extent.latest;
    }
    return Number.isFinite(previousLatest) ? previousLatest : null;
}

export function planLiveAxis({ followLatest, viewport, previousLatest, extent }) {
    if (!extent) {
        return { mode: "none" };
    }
    if (followLatest || !viewport || !Number.isFinite(viewport.min) || !Number.isFinite(viewport.max)) {
        return { mode: "fit", min: extent.min, max: extent.max, resumeFollow: false };
    }
    const fullSpan = Math.max(extent.max - extent.min, 1);
    const span = Math.max(viewport.max - viewport.min, 1);
    const tolerance = liveEdgeTolerance(extent);
    const pinned = Number.isFinite(previousLatest) && viewport.max >= previousLatest - tolerance;
    const latestMoved = Number.isFinite(previousLatest) && extent.latest > previousLatest + 1;
    if (pinned && latestMoved) {
        if (span >= fullSpan * 0.98) {
            return { mode: "fit", min: extent.min, max: extent.max, resumeFollow: true };
        }
        const nextMin = extent.max - span;
        return {
            mode: "slide",
            min: nextMin,
            max: extent.max,
            resumeFollow: false,
        };
    }
    return { mode: "preserve", min: viewport.min, max: viewport.max, resumeFollow: false };
}
